import {html, keyed, LitElement, repeat} from "../../admin/script/libs/lit.dist.js";
import './pk-taggable-text-editor.scss';

class PkTaggableTextEditorTag extends LitElement {
    static get properties() {
        return {
            content: {type: String},
            block: {},
        };
    }
    render() {
        return this.content;
    }
    createRenderRoot() {
        return this;
    }
}

class PkTaggableTextEditorTextTag extends PkTaggableTextEditorTag {
    constructor() {
        super();
        this.contentEditable = 'true';
    }
    render() {
        return this.content;
    }
}

class PkTaggableTextEditorMovableTag extends PkTaggableTextEditorTag {
    constructor() {
        super();
        this.style.userSelect = 'none';
    }
    static get properties() {
        return {
            content: {type: String},
            label: {type: String},
        };
    }
    render() {
        return html`
            <div contenteditable="false" class="badge text-bg-warning d-inline-flex align-items-center">
                <i
                    sortable-handle
                    @touchstart="${() => this.dispatchEvent(new Event('draghandleenter', {bubbles: true}))}"
                    @mouseenter="${() => this.dispatchEvent(new Event('draghandleenter', {bubbles: true}))}"
                    @mouseleave="${() => this.dispatchEvent(new Event('draghandleleave', {bubbles: true}))}"
                    class="bi bi-arrows-move p-2 ms-n2 my-n2 cursor-move d-block"
                ></i>
                <div class="text-left">
                    <div class="label">${this.label}</div>
                    <div class="content">${this.content}</div>
                </div>
                <i 
                    class="bi bi-x ps-2 cursor-pointer"
                    @click="${() => {
                        this.dispatchEvent(new Event('deleteTag', {bubbles: true}))
                    }}"
                ></i>
            </div>
        `;
    }
}

class PkTaggableTextEditor extends LitElement {

    lastCursorPosition = 0;
    lastFocusBlock = undefined;
    isDragging = false;

    static get properties() {
        return {
            content: {type: String},
            name: {type: String},
            value: {type: String},
            tagsLabel: {type: String},
            disabled: {type: Boolean},
            isSorting: {type: Boolean, state: true},
            blocks: {type: Array, state: true},
            getTagType: {type: Function},
            getTagLabel: {type: Function},
            getTagDescription: {type: Function},
            tags: {type: Array},
        };
    }

    constructor() {
        super();
        this.tagsLabel = '';
        this.content = '';
        this.isSorting = false;
        this.getTagType = (content) => content.match(/#\w+#/) ? 'TAG' : 'TEXT';
        this.getTagLabel = (block) => {
            for (let tag of this.tags) {
                if (tag.content === block.content) {
                    return tag.name || '';
                }
            }
            return '';
        };
        this.getTagDescription = (block) => {
            for (let tag of this.tags) {
                if (tag.content === block.content) {
                    return tag.description || block.content;
                }
            }
            return block.content;
        };
        this.tags = [];
    }


    get value() {
        return this.compileValue();
    }

    set value(value) {
        this.blocks = this.createBlocks(value);
    }

    createContentBlock(content) {
        let type = this.getTagType?.(content) || 'TEXT';
        return {
            type,
            content,
        };
    }

    createBlocks(value) {
        value = value || '';
        const blocks = [];

        let currentItem = '';
        let isTag = false;

        let char;
        let previous;
        let newTag;

        const createTag = () => {
            blocks.push(this.createContentBlock(currentItem));
            isTag = false;
            currentItem = '';
        }

        for (let i = 0; i < value.length; i++) {
            newTag = false;
            char = value[i];
            previous = value[i - 1];

            if (char === '#') {
                if (!isTag) {
                    createTag(currentItem);
                    isTag = true;
                    currentItem += char;
                } else {
                    currentItem += char;
                    createTag();
                    isTag = false;
                }
                continue;
            }

            if (this.isSorting) {
                currentItem += char;
                if (char === "\n" || (char === ' ' && previous !== ' ')) {
                    createTag();
                }
                continue
            }

            currentItem += char;
        }

        if (currentItem) {
            blocks.push(this.createContentBlock(currentItem));
        }

        if (blocks[blocks.length - 1]?.type !== 'TEXT') {
            blocks.push(this.createContentBlock(''));
        }

        if (this.isSorting) {
            blocks.push(this.createContentBlock(''));
        }

        return blocks;
    }

    set isSorting(value) {
        if (this.__isSorting !== value) {
            this.__isSorting = value;
            if (value === false) {
                this.isDragging = false;
            }
            this.blocks = this.createBlocks(this.compileValue());
        }
    }

    set getTagType(getter) {
        this.__getTagType = getter;
        this.blocks = this.createBlocks(this.compileValue());
    }

    get getTagType() {
        return this.__getTagType;
    }

    get isSorting() {
        return this.__isSorting;
    }

    compileValue() {
        let content = '';
        for (let block of this.blocks || []) {
            content += block.content || '';
        }
        return content;
    }

    insertNamedTag(tag) {
        const focusedBlock = this.blocks.filter(block => block === this.lastFocusBlock)[0];
        if (focusedBlock) {
            const index = this.blocks.indexOf(focusedBlock);
            this.blocks.splice(index, 1, {
                ...focusedBlock,
                content: focusedBlock.content.substring(0, this.lastCursorPosition),
            },
                this.createContentBlock(tag.content),
                this.createContentBlock(focusedBlock.content.substring(this.lastCursorPosition)),
            );
            this.lastFocusBlock = this.blocks[index + 2];
            this.lastCursorPosition = 0;
            this.requestUpdate();
            setTimeout(() => {
                this.focusOnCurrentBlock();
            }, 0);
        } else {
            this.blocks = [
                ...this.blocks,
                this.createContentBlock(tag.content),
                this.createContentBlock(''),
            ];
            setTimeout(() => {
                this.lastFocusBlock = this.blocks[this.blocks.length - 1];
                this.focusOnLastTextElement();
            }, 100);
        }
        this.dispatchEvent(new Event('input', {bubbles: true}));
    }

    focusOnLastTextElement() {
        const focusedElement = this.querySelector('pk-taggable-text-editor-text-tag:focus-within');
        if (!focusedElement) {
            const editors = this.querySelectorAll('pk-taggable-text-editor-text-tag');
            const editor = editors[editors.length - 1];
            editor?.focus();
            document.getSelection().setPosition(editor.lastChild, editor.textContent.length);
        }
        this.lastCursorPosition = document.getSelection().focusOffset;
    }

    focusOnPreviousTextElement(currentElement) {
        while ((currentElement = currentElement.previousElementSibling) !== null) {
            if (currentElement instanceof PkTaggableTextEditorTextTag) {
                currentElement.focus();
                this.lastCursorPosition = currentElement.textContent.length;
                document.getSelection().setPosition(currentElement.lastChild, this.lastCursorPosition);
                break;
            }
        }
    }

    focusOnNextTextElement(currentElement) {
        while ((currentElement = currentElement.nextElementSibling) !== null) {
            if (currentElement instanceof PkTaggableTextEditorTextTag) {
                currentElement.focus();
                this.lastCursorPosition = 0;
                document.getSelection().setPosition(currentElement.lastChild, 0);
                break;
            }
        }
    }

    focusOnCurrentBlock() {
        if (this.lastFocusBlock) {
            for (let editor of this.querySelectorAll('pk-taggable-text-editor-text-tag')) {
                if (editor.block === this.lastFocusBlock) {
                    editor?.focus();
                    this.lastCursorPosition = 0;
                    document.getSelection().setPosition(editor.lastChild, 0);
                    break;
                }
            }
        }
    }

    deleteBlock(block) {
        const blocks = this.blocks.filter(b => b !== block);
        const content = blocks.map(b => b.content).join('');
        this.blocks = this.createBlocks(content);
        this.dispatchEvent(new Event('input', {bubbles: true}));
    }

    onEditorKeyUpHandler(e) {
        const previousCursorPosition = this.lastCursorPosition;
        this.lastCursorPosition = document.getSelection().focusOffset;

        if (this.lastCursorPosition === 0 && previousCursorPosition === 0 && e.key === 'ArrowLeft') {
            this.focusOnPreviousTextElement(e.target);
        }

        const contentLength = e.target.textContent.length;
        if (this.lastCursorPosition === contentLength && previousCursorPosition === contentLength && e.key === 'ArrowRight') {
            this.focusOnNextTextElement(e.target);
        }
    }

    onBeforeSortHandler() {
        this.isSorting = true;
        this.isDragging = true;
    }

    onSortEndHandler() {
        this.isSorting = false;
        this.isDragging = false;
    }

    onSortHandler(e) {
        e.preventDefault();
        this.isDragging = false;
        this.value = e.target.itemsByRef.join('');
        this.isSorting = false;
        this.dispatchEvent(new Event('input', {bubbles: true}));
    }

    render() {
        return html`
            <pk-sortable
                horizontal
                handle="[sortable-handle]"
                @beforeSortStart="${() => this.onBeforeSortHandler()}"
                @sortEnd="${() => this.onSortEndHandler()}"
                @sort="${e => this.onSortHandler(e)}"
            >
                <p 
                    sortable-parent
                    class="inner-editor form-control ${this.isSorting ? 'is-sorting' : ''}"
                    @keyup="${e => this.onEditorKeyUpHandler(e)}"
                    @click="${() => this.focusOnLastTextElement()}"
                >
                    ${repeat(this.blocks, block => keyed(block.content, html`
                        ${block.type !== 'TEXT' ? html`
                            <pk-taggable-text-editor-movable-tag
                                sortable-item
                                .ref="${block.content}"
                                .block="${block}"
                                .label="${this.getTagLabel?.(block)}"
                                .content="${this.getTagDescription?.(block)}"
                                @draghandleenter="${() => this.isSorting = true}"
                                @deleteTag="${() => this.deleteBlock(block)}"
                            ></pk-taggable-text-editor-movable-tag>
                        ` : html`
                            <pk-taggable-text-editor-text-tag
                                sortable-item
                                .ref="${block.content}"
                                .block="${block}"
                                .content="${block.content}"
                                @click="${() => this.isSorting = false}"
                                @mouseenter="${() => {
                                    if (!this.isDragging) {
                                        this.isSorting = false
                                    }
                                }}"
                                @blur="${() => this.requestUpdate()}"
                                @focus="${() => this.lastFocusBlock = block}"
                                @input="${e => {
                                    const index = this.blocks.indexOf(block);
                                    this.blocks[index].content = e.target.textContent;
                                    this.dispatchEvent(new Event('input', {bubbles: true}));
                                }}"
                            ></pk-taggable-text-editor-text-tag>
                        `}
                    `))}
                </p>
            </pk-sortable>
            <pk-sortable
                    horizontal
                    handle="[sortable-handle]"
                    preventDropOnParent
                    @beforeSortStart="${() => this.onBeforeSortHandler()}"
                    @sortEnd="${() => this.onSortEndHandler()}"
                    @sort="${(e) => e.preventDefault()}"
            >
                <div class="toolbar">
                    <div class="text-muted fs-sm mb-2">${this.tagsLabel || 'Dodaj tag'}</div>
                    ${repeat(this.tags, tag => html`
                        <button
                            sortable-item
                            .ref="${tag.content}"
                            type="button"
                            class="badge pk-taggable-text-editor-movable-tag pk-taggable-text-editor-movable-tag-lg auto-width text-bg-warning d-inline-flex align-items-center my-1"
                            @click="${() => this.insertNamedTag(tag)}"
                        >
                            <i sortable-handle class="bi bi-arrows-move p-2 ms-n2 my-n2 cursor-move d-block"></i>
                            <div class="text-left">
                                <div class="label">${tag.name}</div>
                                <div class="content">${tag.description}</div>
                            </div>
                            <i class="bi bi-plus p-2 me-n2 my-n2 d-block"></i>
                        </button>
                    `)}

                    <div class="text-muted fs-nano mt-2 d-flex align-items-baseline">
                        <i class="bg bi-question-circle-fill me-2"></i> 
                        <div>
                            Kliknij <i class="bi bi-plus"></i> aby dodać w miejsce kursora, lub
                            przeciągnij i upuść (<i class="bi bi-arrows-move"></i>) w wybrane miejsce.
                        </div>
                    </div>
                </div>
            </pk-sortable>
            <input type="hidden" name="${this.name}" .value="${this.value}">
        `;
    }

    createRenderRoot() {
        return this;
    }
}

window.customElements.define('pk-taggable-text-editor-text-tag', PkTaggableTextEditorTextTag);
window.customElements.define('pk-taggable-text-editor-movable-tag', PkTaggableTextEditorMovableTag);
window.customElements.define('pk-taggable-text-editor', PkTaggableTextEditor);
